<template>
  <Teleport to="body">
    <div
      class="sp-overlay"
      v-if="visible"
      @click="handleBackgroundClick"
    >
      <div
        class="sp-modal"
        role="dialog"
        aria-labelledby="sp-modal-title"
        @click.stop
      >
        <!-- ── HEADER ── -->
        <div class="sp-header">
          <button
            v-if="closeButton"
            class="sp-close"
            aria-label="Close"
            @click="closePopup"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="sp-header-content">
            <div class="sp-header-logo">
              <svg viewBox="0 0 515.91 447.87" xmlns="http://www.w3.org/2000/svg">
                <path fill="#fe4d37" d="M475.97,378.61H182.5l29.92-49.58h174.68l-7.34-12.79-77.76-135.66c-4.56-7.94-28.78-50.2-33.85-59.05l-8.03-14-27.11-47.3,31.43-52.53,26.68,48.45,8.03,14,26.83,46.81,7.02,12.24,107.22,187.04,7.33,12.79,28.42,49.58Z" />
                <path fill="#fe4d37" d="M511.26,440.17H75.88l30.12-49.92,7.02-11.64,29.92-49.58,7.72-12.79,77.83-124.55,26.84,46.81-49.59,77.74-7.72,12.79-29.92,49.58-7.02,11.64h321.56l28.62,49.92Z" />
                <path fill="#fe4d37" d="M364.69,316.24h-51.45l-42.81-74.67c-9.82-17.15-32.07-55.93-41.87-73.04l-9.61,15.92-79.52,131.79-7.72,12.79-29.91,49.58-7.02,11.64-30.13,49.92H4.64l30.12-49.92,7.02-11.64,29.92-49.58,7.72-12.79,110.29-182.79,9.61-15.93,26.39-43.73,29.23,51.01c9.81,17.1,32.04,55.91,41.88,73.05l67.87,118.39Z" />
              </svg>
            </div>
            <div class="sp-header-text">
              <div class="sp-eyebrow">Rosterem subscription</div>
              <div class="sp-title" id="sp-modal-title">Employee Scheduling</div>
            </div>
          </div>

          <div
            v-if="accountStatus === 'trial' && daysRemaining > 0"
            class="sp-trial-pill"
          >
            <span class="sp-trial-pill-dot"></span>
            {{ daysRemaining }} days remaining in trial
          </div>
        </div>

        <!-- ── PRICE ── -->
        <div class="sp-price-block">
          <div class="sp-price-row">
            <span class="sp-price-amount">$50.00</span>
            <span class="sp-price-currency">AUD</span>
            <span class="sp-price-period">per month</span>
          </div>
          <div class="sp-price-note">Excludes GST · Billed monthly</div>
        </div>

        <!-- ── DESCRIPTION ── -->
        <div class="sp-description">
          A comprehensive employee scheduling solution. Manage shifts,
          timesheets, leave, and access support with ease.
        </div>

        <!-- ── FEATURES ── -->
        <div class="sp-features">
          <div class="sp-feature" v-for="feature in features" :key="feature">
            <span class="sp-feature-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            {{ feature }}
          </div>
        </div>

        <!-- ── FOOTER ── -->
        <div class="sp-footer">
          <button
            v-if="closeButton"
            class="sp-btn sp-btn-ghost"
            @click="closePopup"
          >
            Maybe later
          </button>
          <Checkout
            :accountStatus="accountStatus"
            :closeButton="closeButton"
            @subscription-success="closePopup"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import Checkout from "@/components/Checkout.vue";

export default {
  components: {
    Checkout,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    daysRemaining: {
      type: Number,
      required: true,
    },
    accountStatus: {
      type: String,
      required: true,
    },
    closeButton: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["closePopup"],
  data() {
    return {
      features: [
        "Employee Scheduling",
        "Employee Shift Management",
        "Timesheets",
        "Leave Management",
        "24/7 Email Support",
        "Reporting",
      ],
    };
  },
  methods: {
    closePopup() {
      this.$emit("closePopup");
    },
    handleBackgroundClick() {
      this.closePopup();
    },
  },
};
</script>

<style scoped>
/*
 * All class names are sp-* prefixed to avoid collision with the global
 * Bootstrap stylesheet (imported in src/main.js), which defines its own
 * .modal, .modal-header, .modal-footer, .btn, etc. and would otherwise
 * leak rules like `display: none` onto our card.
 */

/* ── TOKENS (scoped to this component) ── */
.sp-overlay {
  --green-dark: #0f1f17;
  --green-vivid: #22c55e;
  --green-light: #86efac;
  --green-pale: #dcfce7;
  --green-deep: #16a34a;
  --green-forest: #14532d;
  --red-brand: #fe4d37;
  /* #61 addendum (2026-08-02, Y1): --text-primary/--text-secondary/
     --text-tertiary used to be redeclared here, shadowing main.css's
     global tokens. Deleted — .sp-overlay is Teleported to <body>, still a
     normal descendant of :root, so it already inherits the real global
     values (#111827/#4b5563/#9ca3af). Nothing here depended on the
     shadowed #6b7280 --text-secondary being distinct from the global
     #4b5563 — the global --text-muted token already covers #6b7280 by
     name for anywhere that specifically wants it. */
  --border: #e5e7eb;
  --border-light: #f3f4f6;
  --surface: #f9fafb;
}

/* ── OVERLAY ── */
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 31, 23, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1000;
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── MODAL CARD ── */
.sp-modal {
  display: block;
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  overflow: hidden;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(15, 31, 23, 0.04);
  animation: spModalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes spModalIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ── HEADER (dark green banner) ── */
.sp-header {
  background: var(--green-dark);
  padding: 1.75rem 1.75rem 1.5rem;
  position: relative;
  overflow: hidden;
}
.sp-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 85% 20%, rgba(34, 197, 94, 0.12), transparent 50%),
    radial-gradient(circle at 15% 80%, rgba(254, 77, 55, 0.06), transparent 50%);
  pointer-events: none;
}
.sp-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  z-index: 2;
  padding: 0;
  margin: 0;
}
.sp-close:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
}
.sp-close svg {
  width: 14px;
  height: 14px;
}

.sp-header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 14px;
}
.sp-header-logo {
  width: 44px;
  height: 44px;
  background: rgba(254, 77, 55, 0.12);
  border: 1px solid rgba(254, 77, 55, 0.25);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sp-header-logo svg {
  width: 22px;
  height: 22px;
}
.sp-header-text {
  flex: 1;
  min-width: 0;
}
.sp-eyebrow {
  font-size: 10px;
  font-weight: 600;
  color: var(--green-light);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 3px;
}
.sp-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

/* trial pill */
.sp-trial-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 5px 10px 5px 8px;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.25);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: var(--green-light);
  position: relative;
  z-index: 1;
}
.sp-trial-pill-dot {
  width: 6px;
  height: 6px;
  background: var(--green-vivid);
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}

/* ── PRICE BLOCK ── */
.sp-price-block {
  padding: 1.5rem 1.75rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
}
.sp-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}
.sp-price-amount {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.03em;
  line-height: 1;
}
.sp-price-currency {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.sp-price-period {
  font-size: 13px;
  color: var(--text-secondary);
  margin-left: auto;
}
.sp-price-note {
  font-size: 11px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
}

/* ── DESCRIPTION ── */
.sp-description {
  padding: 1.25rem 1.75rem 1rem;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary);
}

/* ── FEATURE LIST ── */
.sp-features {
  padding: 0 1.75rem 1.5rem;
  display: grid;
  gap: 6px;
}
.sp-feature {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  background: var(--surface);
  border: 1px solid var(--border-light);
  border-radius: 9px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.15s;
}
.sp-feature:hover {
  background: var(--green-pale);
  border-color: rgba(34, 197, 94, 0.25);
}
.sp-feature-check {
  width: 18px;
  height: 18px;
  background: var(--green-pale);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sp-feature-check svg {
  width: 10px;
  height: 10px;
  color: var(--green-deep);
  stroke-width: 3.5;
}

/* ── FOOTER ── */
.sp-footer {
  padding: 1rem 1.75rem 1.25rem;
  background: var(--surface);
  border-top: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 10px;
}
.sp-btn {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  line-height: 1.2;
}
.sp-btn-ghost {
  background: transparent;
  color: var(--text-secondary);
  border-color: var(--border);
}
.sp-btn-ghost:hover {
  background: #fff;
  color: var(--text-primary);
  border-color: #d1d5db;
}

/*
 * The Subscribe / Unsubscribe button is rendered by the shared <Checkout>
 * component, which keeps all billing logic. We restyle its buttons here
 * (scoped via :deep) so they match the footer design without touching the
 * shared component used by ExpiredAccount.vue and OrgSettings.vue.
 */
.sp-footer :deep(.button-green),
.sp-footer :deep(.button-red) {
  flex: 1;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  margin: 0;
  line-height: 1.2;
}
.sp-footer :deep(.button-green) {
  background: var(--green-deep);
  color: #fff;
  box-shadow: 0 1px 2px rgba(22, 163, 74, 0.15);
}
.sp-footer :deep(.button-green:hover:not(:disabled)) {
  background: var(--green-forest);
}
.sp-footer :deep(.button-green:disabled) {
  opacity: 0.7;
  cursor: not-allowed;
}
.sp-footer :deep(.button-red) {
  background: var(--red-brand);
  color: #fff;
}
.sp-footer :deep(.button-red:hover) {
  background: #e23b27;
}

/* ── RESPONSIVE ── */
@media (max-width: 500px) {
  .sp-modal {
    border-radius: 14px;
  }
  .sp-header,
  .sp-price-block,
  .sp-description,
  .sp-features,
  .sp-footer {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
  .sp-title {
    font-size: 16px;
  }
  .sp-price-amount {
    font-size: 28px;
  }
}
</style>
